/** Pure pinch math + DOM multi-pointer tracker (engine input; Phaser-free). */

export const PINCH_MIN_START_DIST = 8;

export function pinchScale(startDist: number, currentDist: number): number {
  if (startDist < PINCH_MIN_START_DIST) return 1;
  return currentDist / startDist;
}

export function pointerPairDistance(
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export type PinchTrackerHandlers = {
  /** Single-finger field gesture (client coords). */
  onSingleDown?: (clientX: number, clientY: number, pointerId: number) => void;
  onSingleMove?: (clientX: number, clientY: number, pointerId: number) => void;
  onSingleUp?: (clientX: number, clientY: number, pointerId: number) => void;
  /** Fired once when a 2-finger pinch session begins (after single is cancelled). */
  onPinchStart?: () => void;
  /** scale = currentDist / startDist (1 = unchanged). */
  onPinchScale?: (scale: number) => void;
  onPinchEnd?: () => void;
};

type Pt = { x: number; y: number };

/**
 * Tracks active pointers on an element.
 * 1 finger → single handlers; 2+ → pinch (cancels single; no resume until all up).
 */
export class PinchTracker {
  private readonly pointers = new Map<number, Pt>();
  private readonly handlers: PinchTrackerHandlers;
  private el: HTMLElement | null = null;
  private pinching = false;
  private pinchStartDist = 0;
  /** After pinch ends with one finger left, ignore until all pointers released. */
  private suppressSingleUntilClear = false;
  private singleActiveId: number | null = null;

  constructor(handlers: PinchTrackerHandlers = {}) {
    this.handlers = handlers;
  }

  get isPinching(): boolean {
    return this.pinching;
  }

  get pointerCount(): number {
    return this.pointers.size;
  }

  attach(el: HTMLElement): void {
    this.detach();
    this.el = el;
    el.addEventListener("pointerdown", this.onDown, { passive: false });
    el.addEventListener("pointermove", this.onMove, { passive: false });
    el.addEventListener("pointerup", this.onUp, { passive: false });
    el.addEventListener("pointercancel", this.onUp, { passive: false });
  }

  detach(): void {
    if (!this.el) return;
    this.el.removeEventListener("pointerdown", this.onDown);
    this.el.removeEventListener("pointermove", this.onMove);
    this.el.removeEventListener("pointerup", this.onUp);
    this.el.removeEventListener("pointercancel", this.onUp);
    this.el = null;
    this.reset();
  }

  /** Test / manual injection without DOM. */
  injectDown(pointerId: number, x: number, y: number): void {
    this.handleDown(pointerId, x, y);
  }

  injectMove(pointerId: number, x: number, y: number): void {
    this.handleMove(pointerId, x, y);
  }

  injectUp(pointerId: number, x: number, y: number): void {
    this.handleUp(pointerId, x, y);
  }

  private reset(): void {
    this.pointers.clear();
    this.pinching = false;
    this.pinchStartDist = 0;
    this.suppressSingleUntilClear = false;
    this.singleActiveId = null;
  }

  private readonly onDown = (ev: PointerEvent): void => {
    ev.preventDefault();
    this.el?.setPointerCapture?.(ev.pointerId);
    this.handleDown(ev.pointerId, ev.clientX, ev.clientY);
  };

  private readonly onMove = (ev: PointerEvent): void => {
    if (!this.pointers.has(ev.pointerId)) return;
    if (this.pinching) ev.preventDefault();
    else if (ev.pointerType === "mouse" && (ev.buttons & 1) === 0) return;
    this.handleMove(ev.pointerId, ev.clientX, ev.clientY);
  };

  private readonly onUp = (ev: PointerEvent): void => {
    try {
      this.el?.releasePointerCapture?.(ev.pointerId);
    } catch {
      /* already released */
    }
    this.handleUp(ev.pointerId, ev.clientX, ev.clientY);
  };

  private handleDown(pointerId: number, x: number, y: number): void {
    this.pointers.set(pointerId, { x, y });
    if (this.pointers.size >= 2) {
      // Abandon single without Up (would look like a tap); pinch start clears field.
      this.singleActiveId = null;
      this.beginPinch();
      return;
    }
    if (this.suppressSingleUntilClear) return;
    this.singleActiveId = pointerId;
    this.handlers.onSingleDown?.(x, y, pointerId);
  }

  private handleMove(pointerId: number, x: number, y: number): void {
    if (!this.pointers.has(pointerId)) return;
    this.pointers.set(pointerId, { x, y });
    if (this.pinching && this.pointers.size >= 2) {
      const pair = this.pairPoints();
      if (!pair) return;
      const dist = pointerPairDistance(pair[0], pair[1]);
      this.handlers.onPinchScale?.(pinchScale(this.pinchStartDist, dist));
      return;
    }
    if (this.singleActiveId === pointerId && !this.suppressSingleUntilClear) {
      this.handlers.onSingleMove?.(x, y, pointerId);
    }
  }

  private handleUp(pointerId: number, x: number, y: number): void {
    if (!this.pointers.has(pointerId)) return;
    this.pointers.delete(pointerId);

    if (this.pinching) {
      if (this.pointers.size < 2) {
        this.pinching = false;
        this.pinchStartDist = 0;
        this.suppressSingleUntilClear = this.pointers.size > 0;
        this.handlers.onPinchEnd?.();
      }
      if (this.pointers.size === 0) this.suppressSingleUntilClear = false;
      return;
    }

    if (this.singleActiveId === pointerId) {
      this.singleActiveId = null;
      this.handlers.onSingleUp?.(x, y, pointerId);
    }
    if (this.pointers.size === 0) this.suppressSingleUntilClear = false;
  }

  private beginPinch(): void {
    const pair = this.pairPoints();
    if (!pair) return;
    this.pinchStartDist = pointerPairDistance(pair[0], pair[1]);
    this.pinching = this.pinchStartDist >= PINCH_MIN_START_DIST;
    if (this.pinching) this.handlers.onPinchStart?.();
  }

  private pairPoints(): [Pt, Pt] | null {
    if (this.pointers.size < 2) return null;
    const pts = [...this.pointers.values()];
    return [pts[0]!, pts[1]!];
  }
}
